import * as R from 'ramda';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataQueryKey } from '../../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../../hooks/useDataQuery/useDataQuery';
import useMoney from '../../../../hooks/useMoney';
import LangEnum from '../../../../lang/LangEnum';
import { useValidateOrderProducts } from '../../../../services/modules/order-product/order-product.queries';
import snackbarStore from '../../../../stores/Snackbar.store';
import { WithId } from '../../../../types/Base.types';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useAuthContext } from '../../../login/AuthContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { ActionEnum } from '../../actions/types/orderActions.types';
import { OrderActionProps } from '../../actions/types/orderActionsContext.types';
import { useOrderContext } from '../../contexts/Order.context';
import { useOrderActionsContext } from '../../contexts/OrderActions.context';
import useOrderView from '../../hooks/useOrderView';
import { OrderView } from '../../ordersView.types';
import { OrderProductsViewActionsEnum } from '../../products/orderProducts.types';
import { ordersRepository } from '../../repositories/ordersRepository/orders.repository';
import BusinessPolicyViolationsModal from '../validate/conditions/businessPolicy/components/BusinessPolicyViolationsModal';
import { useBusinessPolicyViolationsModal } from '../validate/conditions/businessPolicy/hooks/useBusinessPolicyViolationsModal';
import { useOrderProductsBusinessPolicyViolations } from '../validate/conditions/businessPolicy/hooks/useOrderProductsBusinessPolicyViolations';
import { getOrderErrorQuantity } from '../validate/utils/orderValidation.utils';
import {
  OrderConditionErrors,
  OrderEditSectionsContextProps,
  OrderEditSectionsContextProviderProps,
  OrderEditSectionsEnum,
  OrderError,
  OrderExtraInfo,
  OrderSectionErrors,
} from './OrderEditSections.types';

const { handleMessage } = snackbarStore;

export const OrderEditSectionsContext = createContext<OrderEditSectionsContextProps>({
  currentSection: OrderEditSectionsEnum.PRODUCTS,
  handleChangeCurrentSection: () => {
    return;
  },
  rawConditionErrors: [],
  isValidatingOrderConditions: false,
  hasOrderErrors: false,
  hasGeneralDataErrors: false,
  hasConditionsErrors: false,
  conditionsErrors: [],
  generalDataErrors: [],
  productsErrors: {},
  orderProductsWithErrors: {},
  productsBusinessPolicyViolationsByItem: new Map(),
  productsBusinessPolicyViolations: [],
  openPolicyViolationsModal: () => {
    return;
  },
  openPolicyViolationSectionsModal: () => {
    return;
  },
});

export const OrderEditSectionsContextProvider = ({
  children,
  isOfflineMode = false,
}: OrderEditSectionsContextProviderProps) => {
  const [currentSection, setCurrentSection] = useState<OrderEditSectionsEnum>(
    OrderEditSectionsEnum.PRODUCTS,
  );
  const { loggedin } = useAuthContext();
  const params = useParams<WithId>();
  const orderId = params.id;
  const repository = ordersRepository(orderId);
  const { translate } = useTranslation();
  const { formatMoney } = useMoney();
  const { userRole } = useApplicationContext();
  const { changeOrderReviewAction, order } = useOrderContext();
  const orderView = useOrderView();
  const isFinishedOrder = orderView === OrderView.FINISHED;
  const { onClickOrderAction } = useOrderActionsContext();
  const [hasValidateConditionsIntegrationError, setHasValidateConditionsIntegrationError] =
    useState<boolean>(false);

  const validateProductsParams = { orderId: params.id, userRole };
  const validateProductsOptions = {
    enabled: currentSection === OrderEditSectionsEnum.PRODUCTS && loggedin && !isFinishedOrder,
  };
  const productValidations = useValidateOrderProducts({
    params: validateProductsParams,
    options: validateProductsOptions,
    isOfflineMode,
  });

  const generalDataErrorsQuery = useDataQuery<OrderSectionErrors | undefined>(
    {
      queryKey: [DataQueryKey.ORDER_VALIDATE_GENERAL_DATA, orderId],
      queryFn: async () => {
        const response = await repository.getOrderGeneralDataErrors();
        if (response.ok) {
          return response.data;
        }
      },
      gcTime: 0,
    },
    {
      enabled: loggedin && !isOfflineMode && !isFinishedOrder,
    },
  );

  const conditionsErrorsQuery = useDataQuery<OrderConditionErrors | undefined>(
    {
      queryKey: [DataQueryKey.ORDER_VALIDATE_CONDITIONS, orderId],
      queryFn: async () => {
        setHasValidateConditionsIntegrationError(false);

        const response = await repository.getOrderConditionsErrors();
        if (response.ok) {
          return response.data;
        }

        if (response.data?.code === LangEnum.API_EXCEPTION_INTEGRATION_SERVER_UNAVAILABLE) {
          setHasValidateConditionsIntegrationError(true);
          handleMessage({
            type: 'error',
            message: translate(LangEnum.API_EXCEPTION_INTEGRATION_SERVER_UNAVAILABLE),
          });
        }
      },
      gcTime: 0,
    },
    {
      enabled: loggedin && !isOfflineMode && !isFinishedOrder,
    },
  );

  const hasGeneralDataErrors =
    !!generalDataErrorsQuery.data && generalDataErrorsQuery.data?.errors?.length > 0;
  const hasProductErrors =
    Object.keys(productValidations.data?.errorsWithQuantities || {}).length > 0 ||
    !!productValidations.data?.businessPolicyErrors;
  const hasConditionsErrors =
    conditionsErrorsQuery.data?.errors.some(({ errors }) => errors.length > 0) ||
    hasValidateConditionsIntegrationError;
  const hasOrderErrors = hasGeneralDataErrors || hasProductErrors || hasConditionsErrors;

  const rawConditionErrors = useMemo(
    () => conditionsErrorsQuery.data?.errors || [],
    [conditionsErrorsQuery.data?.errors],
  );

  const orderProductsWithErrors = productValidations.data?.orderProductsWithErrors || {};

  const {
    violations: productsBusinessPolicyViolations,
    byItemKey: productsBusinessPolicyViolationsByItem,
  } = useOrderProductsBusinessPolicyViolations(productValidations.data?.businessPolicyErrors);
  const policyViolationsModal = useBusinessPolicyViolationsModal();

  const getTranslatedErrors = useCallback(
    (errors: OrderError[]): string[] =>
      errors.map((err) => {
        const innerQuantity = getOrderErrorQuantity({
          extra: err.extra as OrderExtraInfo,
          formatMoney,
          key: 'quantity',
        });
        const innerExpected = getOrderErrorQuantity({
          extra: err.extra as OrderExtraInfo,
          formatMoney,
          key: 'expected',
        });

        return translate(err.messageCode, {
          quantity: innerQuantity,
          expected: innerExpected,
        });
      }),
    [formatMoney, translate],
  );

  const getErrorMessagesBySection = useCallback(
    ({ errors }: { errors: OrderError[] }): string[] => {
      const mainErrors = getTranslatedErrors(errors);

      const detailsErrors = errors.filter((err) => !!err.details);
      const detailedErrors = getTranslatedErrors(detailsErrors);

      return [...mainErrors, ...detailedErrors];
    },
    [getTranslatedErrors],
  );

  const getConditionsErrorMessages = useMemo(() => {
    const errors: string[] = [];
    rawConditionErrors.map((orderCondition) => {
      const orderDeliveryWindowErrors = getErrorMessagesBySection({
        errors: orderCondition.errors,
      });
      return errors.push(...orderDeliveryWindowErrors);
    });
    return R.uniqBy((err) => err, errors) as string[];
  }, [getErrorMessagesBySection, rawConditionErrors]);

  const generalDataCallback = useCallback(() => {
    setCurrentSection(OrderEditSectionsEnum.GENERAL_DATA);
    if (!isFinishedOrder) {
      generalDataErrorsQuery.refetch();
    }
  }, [isFinishedOrder, generalDataErrorsQuery.refetch]);

  const productsCallback = useCallback(() => {
    setCurrentSection(OrderEditSectionsEnum.PRODUCTS);
    changeOrderReviewAction(OrderProductsViewActionsEnum.EXPAND_ALL);
    if (!isFinishedOrder) {
      productValidations.refetch();
    }
  }, [isFinishedOrder, changeOrderReviewAction, productValidations.refetch]);

  const updateOrVerifyOrder = useCallback(async () => {
    if (order && !isFinishedOrder) {
      await onClickOrderAction(
        ActionEnum.VALIDATE_OR_UPDATE_ORDER,
        OrderActionProps.orderToProps(order),
      );
    }
  }, [order, isFinishedOrder, onClickOrderAction]);

  const conditionsCallback = useCallback(() => {
    setCurrentSection(OrderEditSectionsEnum.ORDER_CONDITIONS);
    if (!isFinishedOrder) {
      generalDataErrorsQuery.refetch();
      conditionsErrorsQuery.refetch();
    }
    updateOrVerifyOrder();
  }, [
    isFinishedOrder,
    updateOrVerifyOrder,
    conditionsErrorsQuery.refetch,
    generalDataErrorsQuery.refetch,
  ]);

  const handleChangeCurrentSection = useCallback(
    (section: OrderEditSectionsEnum) => {
      if (currentSection === section) {
        return;
      }
      const callbacks = {
        [OrderEditSectionsEnum.GENERAL_DATA]: generalDataCallback,
        [OrderEditSectionsEnum.PRODUCTS]: productsCallback,
        [OrderEditSectionsEnum.ORDER_CONDITIONS]: conditionsCallback,
      };
      callbacks[section]();
    },
    [currentSection, generalDataCallback, productsCallback, conditionsCallback],
  );

  const generalDataErrors = useMemo(
    () =>
      getErrorMessagesBySection({
        errors: generalDataErrorsQuery.data?.errors || [],
      }),
    [generalDataErrorsQuery.data?.errors, getErrorMessagesBySection],
  );

  const value: OrderEditSectionsContextProps = {
    currentSection,
    handleChangeCurrentSection,
    generalDataErrors,
    productsErrors: productValidations.data?.errorsWithQuantities || {},
    productsBusinessPolicyViolationsByItem,
    productsBusinessPolicyViolations,
    openPolicyViolationsModal: policyViolationsModal.openViolations,
    openPolicyViolationSectionsModal: policyViolationsModal.openSections,
    conditionsErrors: getConditionsErrorMessages,
    orderProductsWithErrors,
    isValidatingOrderConditions: conditionsErrorsQuery.isFetching,
    hasOrderErrors,
    hasGeneralDataErrors,
    hasConditionsErrors,
    rawConditionErrors,
    rawGeneralDataErrors: generalDataErrorsQuery.data?.errors,
  };

  return (
    <OrderEditSectionsContext.Provider value={value}>
      {children}
      <BusinessPolicyViolationsModal
        isOpen={policyViolationsModal.isOpen}
        onClose={policyViolationsModal.closeModal}
        sections={policyViolationsModal.sections}
      />
    </OrderEditSectionsContext.Provider>
  );
};

export const useOrderEditSectionsContext = () =>
  useContext<OrderEditSectionsContextProps>(OrderEditSectionsContext);
